import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  ArrowLeft,
  Stethoscope,
  ClipboardList,
  Droplet,
  TrendingUp,
  UtensilsCrossed,
  HeartPulse,
  CalendarClock,
  AlertTriangle,
  AlertOctagon,
  Info,
  FileDown,
  Loader2,
  Pencil,
  Trash2,
  Upload,
  Check,
  BellRing,
  ShieldCheck,
} from "lucide-react";
import RequireAuth from "@/components/silentscan/RequireAuth";
import { downloadDoctorSummaryPDF } from "@/lib/pdfReport";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiabetesCentre() {
  return (
    <RequireAuth title="Diabetes Centre is private">
      <CentreInner />
    </RequireAuth>
  );
}

const LOW_MSG =
  "Your recorded blood glucose is below 60 mg/dL. This can be concerning, especially if you have symptoms or take glucose-lowering medication. Follow your healthcare professional's instructions and seek prompt medical advice. If you have severe symptoms, seek emergency medical care.";
const HIGH_MSG =
  "Your recorded blood glucose is above 180 mg/dL. The meaning of this result depends on when the measurement was taken, your medical history, treatment plan, and your doctor's instructions. Please follow your healthcare professional's monitoring plan and discuss concerning or repeated readings with them.";
const PATTERN_MSG =
  "Your recent readings show a pattern that may need discussion with your healthcare professional.";
const GATE_MSG =
  "Please upload or enter your doctor's report before using personalized Diabetes Centre guidance.";
const UNCLEAR_MSG =
  "Your doctor's report could not be reliably interpreted. Please confirm the information with your healthcare professional.";

function CentreInner() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportUnclear, setReportUnclear] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/diabetes/state`, { withCredentials: true });
      setState(data);
    } catch {
      toast.error("Could not load the Diabetes Centre");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const report = state?.report || null;
  const readings = state?.readings || [];
  const plans = state?.plans || [];
  const latestPlan = plans[0] || null;
  const reminder = state?.reminder || {};
  const latestReading = readings[0] || null;

  return (
    <div data-testid="diabetes-centre" className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-3xl mx-auto px-5 pt-8 pb-16">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to dashboard
        </Link>

        {/* Header */}
        <div className="mt-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">
            <HeartPulse className="w-3.5 h-3.5" />
            Diabetes Centre
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">
            Monitoring support, guided by your doctor
          </h1>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
            This centre organizes your doctor's documented guidance and your
            glucose readings. It never diagnoses, never changes treatment, and
            never replaces your healthcare professional.
          </p>
        </div>

        {/* Status header */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <StatusCard
            testid="status-report"
            label="Doctor Report"
            ok={!!report}
            value={report ? "Uploaded" : "Not uploaded"}
            sub={report?.condition_as_written || "Required for personalized guidance"}
          />
          <StatusCard
            testid="status-last-reading"
            label="Last glucose reading"
            ok={!!latestReading}
            value={latestReading ? `${latestReading.value} ${latestReading.unit}` : "—"}
            sub={latestReading ? `${latestReading.date} · ${latestReading.context_label}` : "No readings yet"}
          />
          <StatusCard
            testid="status-updated"
            label="Last updated"
            ok={!!latestReading}
            value={latestReading ? latestReading.date : "—"}
            sub={latestReading ? `at ${latestReading.time}` : ""}
          />
          <StatusCard
            testid="status-reminder"
            label="Next monitoring reminder"
            ok={!reminder.due}
            value={reminder.due ? "Due now" : reminder.next_due_date || "—"}
            sub={
              reminder.doctor_set
                ? `Doctor's schedule: every ${reminder.frequency_days} days`
                : "Default check-in rhythm"
            }
            warn={!!reminder.due}
          />
        </div>

        {/* IMPORTANT ALERTS */}
        <Alerts
          latestReading={latestReading}
          readings={readings}
          patternAlert={state?.pattern_alert}
          reminder={reminder}
        />

        {/* DOCTOR'S REPORT */}
        <Section icon={Stethoscope} title="Doctor's Documented Information" testid="doctor-report-section">
          {report ? (
            <ReportView report={report} onChanged={load} />
          ) : (
            <div>
              <div
                data-testid="report-gate-message"
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
              >
                {GATE_MSG}
              </div>
              <ReportForm
                onDone={(ok) => {
                  setReportUnclear(!ok);
                  if (ok) load();
                }}
              />
            </div>
          )}
          {reportUnclear && !report && (
            <div
              data-testid="report-unclear-message"
              className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              {UNCLEAR_MSG}
            </div>
          )}
        </Section>

        {/* GLUCOSE HISTORY */}
        <Section icon={Droplet} title="My Glucose History" testid="glucose-history-section">
          <GlucoseForm onSaved={load} />
          <GlucoseTable readings={readings} onChanged={load} />
        </Section>

        {/* GLUCOSE TREND */}
        <Section icon={TrendingUp} title="Glucose Trend" testid="glucose-trend-section">
          {readings.length < 2 ? (
            <p className="text-sm text-slate-500">
              Add at least two readings to see your trend chart.
            </p>
          ) : (
            <>
              <div className="h-56 w-full" data-testid="glucose-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={[...readings].reverse().map((r) => ({
                      when: `${(r.date || "").slice(5)} ${r.time || ""}`,
                      mgdl: r.value_mgdl,
                      label: `${r.value} ${r.unit} · ${r.context_label}`,
                    }))}
                    margin={{ top: 8, right: 12, left: -14, bottom: 0 }}
                  >
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="when" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={46} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12 }}
                      formatter={(v, _n, item) => [`${Math.round(v)} mg/dL — ${item.payload.label}`, "Reading"]}
                    />
                    <ReferenceLine y={60} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "60", fontSize: 9, fill: "#dc2626" }} />
                    <ReferenceLine y={180} stroke="#d97706" strokeDasharray="4 4" label={{ value: "180", fontSize: 9, fill: "#d97706" }} />
                    <Line type="monotone" dataKey="mgdl" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: "#059669" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-slate-500 italic">
                Glucose trend — for monitoring and discussion with your healthcare professional.
              </p>
            </>
          )}
        </Section>

        {/* PERSONALIZED GUIDANCE */}
        <Section icon={UtensilsCrossed} title="Personalized Diet Support" testid="diet-support-section">
          {!report ? (
            <div className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-600">
              {GATE_MSG}
            </div>
          ) : (
            <GuidancePanel report={report} latestPlan={latestPlan} plans={plans} readings={readings} onGenerated={load} />
          )}
        </Section>

        {/* DOCTOR'S INSTRUCTIONS + FOLLOW-UP */}
        {report && (
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Section icon={ClipboardList} title="Doctor's Instructions" testid="doctor-instructions-section" compact>
              {report.instructions?.length ? (
                <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-4">
                  {report.instructions.map((i, idx) => (
                    <li key={idx}>{i}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">No specific instructions were documented in the report.</p>
              )}
            </Section>
            <Section icon={CalendarClock} title="Follow-Up" testid="followup-section" compact>
              <p className="text-sm text-slate-700">
                {report.follow_up || "No follow-up schedule was documented in the report."}
              </p>
              {report.monitoring_frequency_days && (
                <p className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  Your doctor has specified monitoring every {report.monitoring_frequency_days} days — that schedule takes priority over generic reminders.
                </p>
              )}
            </Section>
          </div>
        )}

        <p className="mt-8 text-[11px] leading-relaxed text-slate-400">
          SilentScan AI does not replace your doctor. All guidance here is
          supportive lifestyle information based on your doctor's documented
          report and your own readings. In a medical emergency in India, call
          112. This website is not an emergency medical service.
        </p>
      </div>
    </div>
  );
}

/* ---------------- status + layout primitives ---------------- */

const StatusCard = ({ label, value, sub, ok, warn, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border p-4 bg-white shadow-sm ${
      warn ? "border-amber-300" : ok ? "border-emerald-200" : "border-slate-200"
    }`}
  >
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div
      className={`mt-1 text-xl font-bold ${
        warn ? "text-amber-600" : ok ? "text-emerald-700" : "text-slate-400"
      }`}
    >
      {value}
    </div>
    {sub && <div className="mt-0.5 text-xs text-slate-500 truncate">{sub}</div>}
  </div>
);

const Section = ({ icon: Icon, title, testid, children, compact }) => (
  <section
    data-testid={testid}
    className={`mt-4 rounded-2xl border border-slate-200 bg-white shadow-sm ${compact ? "p-5" : "p-6"}`}
  >
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-9 h-9 rounded-xl bg-emerald-600/10 border border-emerald-600/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-emerald-700" />
      </div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    </div>
    {children}
  </section>
);

/* ---------------- alerts ---------------- */

const Alerts = ({ latestReading, readings, patternAlert, reminder }) => {
  const items = [];
  if (latestReading?.flag === "low") items.push({ level: "urgent", text: LOW_MSG, id: "low" });
  if (latestReading?.flag === "high") items.push({ level: "attention", text: HIGH_MSG, id: "high" });
  if (patternAlert) items.push({ level: "attention", text: PATTERN_MSG, id: "pattern" });
  if (reminder?.due && !reminder?.doctor_set) {
    items.push({
      level: "info",
      id: "reminder",
      text: `Blood glucose update due. ${
        reminder.last_reading_date
          ? `Your last recorded reading was on ${reminder.last_reading_date}.`
          : "You have not recorded a reading yet."
      } Consider updating your record and following your healthcare professional's monitoring instructions.`,
    });
  }
  if (reminder?.due && reminder?.doctor_set) {
    items.push({
      level: "info",
      id: "reminder-doctor",
      text: `A reading is due based on your doctor's documented monitoring schedule (every ${reminder.frequency_days} days). Your doctor's schedule takes priority.`,
    });
  }
  if (!items.length) return null;
  const styles = {
    urgent: { wrap: "border-red-300 bg-red-50", icon: "text-red-600", text: "text-red-900", Icon: AlertOctagon },
    attention: { wrap: "border-amber-300 bg-amber-50", icon: "text-amber-600", text: "text-amber-900", Icon: AlertTriangle },
    info: { wrap: "border-blue-200 bg-blue-50", icon: "text-blue-600", text: "text-blue-900", Icon: BellRing },
  };
  return (
    <div data-testid="important-alerts" className="mt-4 space-y-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1">
        <AlertTriangle className="w-3.5 h-3.5" />
        Important alerts
      </div>
      {items.map((it) => {
        const S = styles[it.level];
        return (
        <div
          key={it.id}
          data-testid={`alert-${it.id}`}
          className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${S.wrap}`}
        >
          <S.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${S.icon}`} />
          <p className={`text-sm leading-relaxed ${S.text}`}>
            {it.text}
            {it.level === "urgent" && (
              <span className="block mt-1 font-semibold">In India, call 112 for medical emergencies.</span>
            )}
          </p>
        </div>
        );
      })}
    </div>
  );
};

/* ---------------- doctor report ---------------- */

const ReportView = ({ report, onChanged }) => {
  const [replacing, setReplacing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/diabetes/report`, { withCredentials: true });
      toast.success("Doctor's report removed");
      setConfirmRemove(false);
      onChanged();
    } catch {
      toast.error("Could not remove the report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="report-condition"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-sm font-semibold text-emerald-800"
        >
          <Check className="w-3.5 h-3.5" />
          {report.condition_as_written || "Documented"}
        </span>
        <span className="text-xs text-slate-400">
          saved {report.updated_at ? new Date(report.updated_at).toLocaleDateString("en-IN") : ""} · via {report.source === "photo" ? "photo" : "text"}
        </span>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        {!!report.avoid_foods?.length && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Foods to avoid (doctor-documented)</div>
            <p className="mt-1 text-rose-900">{report.avoid_foods.join(", ")}</p>
          </div>
        )}
        {!!report.prefer_foods?.length && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Foods recommended</div>
            <p className="mt-1 text-emerald-900">{report.prefer_foods.join(", ")}</p>
          </div>
        )}
        {!!report.activity_restrictions?.length && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Activity restrictions</div>
            <p className="mt-1 text-amber-900">{report.activity_restrictions.join("; ")}</p>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid="replace-report-btn"
          onClick={() => setReplacing((r) => !r)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          {replacing ? "Cancel" : "Replace report"}
        </button>
        <button
          data-testid="remove-report-btn"
          onClick={() => setConfirmRemove(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-300 text-xs font-semibold text-rose-700 hover:bg-rose-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Remove
        </button>
      </div>

      {replacing && (
        <div className="mt-4">
          <ReportForm onDone={(ok) => { if (ok) { setReplacing(false); onChanged(); } }} />
        </div>
      )}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent data-testid="confirm-remove-report">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove doctor's report?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this health record?
              Personalized guidance will pause until a new report is added.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-remove-report-yes" onClick={remove} disabled={busy} className="bg-rose-600 text-white hover:bg-rose-700">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ReportForm = ({ onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submitText = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/diabetes/report`,
        { report_text: text.trim() },
        { withCredentials: true }
      );
      if (data.interpreted) {
        toast.success("Doctor's report saved");
        onDone(true);
      } else {
        onDone(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the report");
    } finally {
      setBusy(false);
    }
  };

  const submitFile = async (f) => {
    if (!f || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", f);
      const { data } = await axios.post(`${API}/diabetes/report-file`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.interpreted) {
        toast.success("Doctor's report saved");
        onDone(true);
      } else {
        onDone(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the report photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submitText} className="mt-4 space-y-3" data-testid="report-form">
      <textarea
        data-testid="report-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder="Type or paste your doctor's report here — e.g., 'Pre-diabetes. Reduce added sugar. Increase physical activity. Review in 3 months.'"
        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-emerald-500"
      />
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="report-submit-btn"
          type="submit"
          disabled={busy || text.trim().length < 20}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
          Save doctor's report
        </button>
        <button
          data-testid="report-photo-btn"
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          Upload report photo
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="report-file-input"
          onChange={(e) => submitFile(e.target.files?.[0])}
        />
      </div>
      <p className="text-[11px] text-slate-400">
        The system reads only what is written. If the report can't be reliably
        interpreted, you'll be asked to confirm with your healthcare
        professional — it will never guess.
      </p>
    </form>
  );
};

/* ---------------- glucose ---------------- */

const emptyForm = () => ({
  reading_id: null,
  value: "",
  unit: "mg/dL",
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 5),
  context: "fasting",
});

const GlucoseForm = ({ onSaved, editValue, onDoneEdit }) => {
  const [form, setForm] = useState(editValue || emptyForm());
  const [busy, setBusy] = useState(false);
  const editing = !!form.reading_id;

  useEffect(() => {
    if (editValue) setForm(editValue);
  }, [editValue]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const value = Number(form.value);
    if (!form.value || Number.isNaN(value) || value <= 0) {
      toast.error("Enter a valid glucose value");
      return;
    }
    setBusy(true);
    try {
      const payload = { value, unit: form.unit, date: form.date, time: form.time, context: form.context };
      if (editing) {
        await axios.put(`${API}/diabetes/readings/${form.reading_id}`, payload, { withCredentials: true });
        toast.success("Reading updated");
      } else {
        const { data } = await axios.post(`${API}/diabetes/readings`, payload, { withCredentials: true });
        if (data.reading?.flag === "low") toast.error("Reading below 60 mg/dL — see the safety notice above", { duration: 6000 });
        else if (data.reading?.flag === "high") toast.warning("Reading above 180 mg/dL — see the context notice above", { duration: 6000 });
        else toast.success("Reading saved");
      }
      setForm(emptyForm());
      onDoneEdit?.();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the reading");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="glucose-form"
      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
    >
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Blood glucose value</label>
          <input
            data-testid="glucose-value-input"
            type="number"
            step="0.1"
            min="1"
            value={form.value}
            onChange={(e) => set("value", e.target.value)}
            placeholder="e.g., 105"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-lg font-bold text-slate-900 outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Unit</label>
          <select
            data-testid="glucose-unit-select"
            value={form.unit}
            onChange={(e) => set("unit", e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
          >
            <option>mg/dL</option>
            <option>mmol/L</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Date</label>
          <input
            data-testid="glucose-date-input"
            type="date"
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Time</label>
          <input
            data-testid="glucose-time-input"
            type="time"
            value={form.time}
            onChange={(e) => set("time", e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Measurement context</label>
          <select
            data-testid="glucose-context-select"
            value={form.context}
            onChange={(e) => set("context", e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
          >
            <option value="fasting">Fasting</option>
            <option value="before_meal">Before meal</option>
            <option value="after_meal">After meal</option>
            <option value="random">Random / other</option>
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          data-testid="glucose-save-btn"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Droplet className="w-4 h-4" />}
          {editing ? "Update reading" : "Save reading"}
        </button>
        {editing && (
          <button
            type="button"
            data-testid="glucose-cancel-edit-btn"
            onClick={() => {
              setForm(emptyForm());
              onDoneEdit?.();
            }}
            className="px-4 py-2.5 rounded-full border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-100"
          >
            Cancel edit
          </button>
        )}
      </div>
    </form>
  );
};

const GlucoseTable = ({ readings, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/diabetes/readings/${confirmDel.reading_id}`, { withCredentials: true });
      toast.success("Reading permanently deleted");
      setConfirmDel(null);
      setEditing(null); // close any open edit form so stale values never linger
      onChanged();
    } catch {
      toast.error("Could not delete the reading");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      {editing && (
        <div className="mb-4">
          <GlucoseForm
            editValue={{
              reading_id: editing.reading_id,
              value: String(editing.value),
              unit: editing.unit,
              date: editing.date,
              time: editing.time,
              context: editing.context,
            }}
            onSaved={onChanged}
            onDoneEdit={() => setEditing(null)}
          />
        </div>
      )}
      {!readings.length ? (
        <p className="text-sm text-slate-500">No readings yet — add your first one above.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="glucose-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 font-semibold">Date</th>
                <th className="py-2 pr-3 font-semibold">Time</th>
                <th className="py-2 pr-3 font-semibold">Reading</th>
                <th className="py-2 pr-3 font-semibold">Context</th>
                <th className="py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {readings.map((r) => (
                <tr
                  key={r.reading_id}
                  data-testid={`glucose-row-${r.reading_id}`}
                  className={`border-b border-slate-100 ${
                    r.flag === "low" ? "bg-red-50" : r.flag === "high" ? "bg-amber-50" : ""
                  }`}
                >
                  <td className="py-2.5 pr-3 text-slate-700 whitespace-nowrap">{r.date}</td>
                  <td className="py-2.5 pr-3 text-slate-700 whitespace-nowrap">{r.time}</td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`text-lg font-bold ${
                        r.flag === "low" ? "text-red-700" : r.flag === "high" ? "text-amber-700" : "text-slate-900"
                      }`}
                    >
                      {r.value}
                    </span>{" "}
                    <span className="text-xs text-slate-500">{r.unit}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600">{r.context_label}</td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button
                      data-testid={`edit-reading-${r.reading_id}`}
                      onClick={() => setEditing(r)}
                      className="p-1.5 text-slate-400 hover:text-emerald-700 transition-colors"
                      aria-label="Edit reading"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-reading-${r.reading_id}`}
                      onClick={() => setConfirmDel(r)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors"
                      aria-label="Delete reading"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="confirm-delete-reading">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this reading?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this health record?
              {confirmDel ? ` (${confirmDel.value} ${confirmDel.unit} · ${confirmDel.date})` : ""} This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-reading-yes" onClick={doDelete} disabled={busy} className="bg-rose-600 text-white hover:bg-rose-700">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ---------------- guidance ---------------- */

const GuidancePanel = ({ report, latestPlan, plans, readings, onGenerated }) => {
  const [allergies, setAllergies] = useState("");
  const [preferences, setPreferences] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewPlan, setViewPlan] = useState(null); // older version being viewed
  const shown = viewPlan || latestPlan;

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/diabetes/guidance`,
        { allergies: allergies.trim(), preferences: preferences.trim() },
        { withCredentials: true }
      );
      toast.success("New guidance version created — previous versions are kept");
      setViewPlan(null);
      onGenerated();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(err?.response?.data?.detail || "Could not generate guidance right now");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          data-testid="allergies-input"
          value={allergies}
          onChange={(e) => setAllergies(e.target.value)}
          placeholder="Food allergies (e.g., peanuts, shellfish)"
          className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
        />
        <input
          data-testid="preferences-input"
          value={preferences}
          onChange={(e) => setPreferences(e.target.value)}
          placeholder="Preferences (e.g., vegetarian, South Indian)"
          className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500"
        />
      </div>
      <button
        data-testid="generate-guidance-btn"
        onClick={generate}
        disabled={busy}
        className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HeartPulse className="w-4 h-4" />}
        {latestPlan ? "Update my supportive guidance" : "Generate my supportive guidance"}
      </button>

      {shown && (
        <div className="mt-5" data-testid="guidance-plan">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-bold text-slate-900">
              {shown.label}
              <span className="ml-2 text-xs font-normal text-slate-500">
                based on the doctor's report{shown.based_on?.readings_used ? ` + ${shown.based_on.readings_used} readings` : ""}
              </span>
            </div>
            {viewPlan && (
              <button
                onClick={() => setViewPlan(null)}
                className="text-xs text-emerald-700 hover:underline"
                data-testid="back-to-latest-plan"
              >
                Back to latest plan
              </button>
            )}
          </div>

          {shown.why_changed && (
            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="why-changed">
              <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">
                Why your guidance changed
              </div>
              <p className="mt-1 text-sm text-blue-900">{shown.why_changed}</p>
            </div>
          )}

          {shown.pattern_note && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="pattern-note">
              {shown.pattern_note}
            </div>
          )}

          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-2">Diet support</div>
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-4">
                {(shown.diet_support || []).map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-2">Lifestyle support</div>
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-4">
                {(shown.lifestyle_support || []).map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          </div>

          {!!shown.questions_for_doctor?.length && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Questions for your doctor</div>
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-4">
                {shown.questions_for_doctor.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-4 text-[11px] text-slate-500 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
            These are supportive lifestyle suggestions based on the information
            documented in your doctor's report. They are not a diagnosis or
            treatment, and your doctor's instructions always take priority.
          </p>
        </div>
      )}

      {plans.length > 1 && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="plan-versions">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Previous plan versions
          </div>
          <div className="space-y-1.5">
            {plans.slice(viewPlan ? 0 : 1).map((p) => (
              <button
                key={p.plan_id}
                data-testid={`plan-version-${p.plan_id}`}
                onClick={() => setViewPlan(p)}
                className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition-colors ${
                  shown?.plan_id === p.plan_id
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {p.label} — {p.why_changed?.slice(0, 90) || "Supportive guidance"}
                {p.why_changed?.length > 90 ? "…" : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        data-testid="doctor-summary-btn"
        onClick={() => downloadDoctorSummaryPDF({ report, readings, plan: latestPlan })}
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors"
      >
        <FileDown className="w-4 h-4" />
        Prepare Summary for Doctor
      </button>
      <p className="mt-1.5 text-[11px] text-slate-400">
        AI-generated support summary as a pdf — prepared for discussion with
        your healthcare professional.
      </p>
    </div>
  );
};
